import type {
  LearningSession,
  ReadinessCategory,
  StageProgress,
} from './types.ts';

export const readinessFor = (session: LearningSession): ReadinessCategory => {
  const evidence: string[] = [];
  const missing: string[] = [];
  const concept = session.stages.concept;
  const application = session.stages.application;
  const transfer = session.stages.transfer;
  const support = (progress: StageProgress): string => {
    const tutorTurns = progress.attempts.filter(
      (entry) => entry.kind === 'tutor',
    ).length;
    return `${progress.hintsUsed} hint${progress.hintsUsed === 1 ? '' : 's'} and ${tutorTurns} tutor turn${tutorTurns === 1 ? '' : 's'}`;
  };
  const tutorTurns = (progress: StageProgress): number =>
    progress.attempts.filter((entry) => entry.kind === 'tutor').length;
  const incorrectAttempts = (progress: StageProgress): number =>
    progress.attempts.filter((entry) => entry.correct === false).length;
  const incorrectCopy = (progress: StageProgress): string => {
    const count = incorrectAttempts(progress);
    return `${count} incorrect attempt${count === 1 ? '' : 's'} recorded`;
  };
  const transferHasNoRecordedSupport =
    transfer.hintsUsed === 0 && tutorTurns(transfer) === 0;
  const learningAttemptKinds = new Set([
    'operation',
    'candidate',
    'unknown',
    'model',
    'explanation',
    'unit',
  ]);
  const hasLearningEvidence = Object.values(session.stages).some((progress) =>
    progress.attempts.some((attempt) => learningAttemptKinds.has(attempt.kind)),
  );
  if (concept.complete)
    evidence.push(
      `Concept demonstrated with ${support(concept)} recorded; ${incorrectCopy(concept)}.`,
    );
  else
    missing.push(
      'Concept isolation, original-equation substitution, and full-scope explanation.',
    );
  if (application.complete)
    evidence.push(
      `Application model, equation trail, and hours interpretation demonstrated with ${support(application)} recorded; ${incorrectCopy(application)}.`,
    );
  else
    missing.push(
      'Application model, reversible equation trail, candidate check, and units.',
    );
  if (transfer.complete && transferHasNoRecordedSupport) {
    evidence.push(
      `Changed problem completed with no recorded hints or tutor turns; ${incorrectCopy(transfer)}.`,
    );
    missing.push(
      'An explicit unprompted or delayed transfer check is still needed before describing independent transfer.',
    );
  } else if (transfer.complete) {
    evidence.push(
      `Changed-coefficient transfer completed with ${support(transfer)} recorded; ${incorrectCopy(transfer)}.`,
    );
    missing.push('A later explicit unprompted or delayed transfer check.');
  } else
    missing.push(
      'Completion of the changed rental problem, followed later by an explicit unprompted or delayed transfer check.',
    );
  const category =
    transfer.complete && transferHasNoRecordedSupport
      ? 'transfer-demonstrated'
      : transfer.complete
        ? 'supported-transfer-demonstrated'
        : application.complete
          ? 'application-demonstrated'
          : hasLearningEvidence
            ? 'developing-evidence'
            : 'needs-evidence';
  const label =
    category === 'transfer-demonstrated'
      ? 'Changed problem completed with no recorded hints or tutor turns'
      : category === 'supported-transfer-demonstrated'
        ? 'Transfer completed with recorded support; an independent check is still needed'
        : category === 'application-demonstrated'
          ? 'Application demonstrated; later transfer still needed'
          : category === 'developing-evidence'
            ? 'Some evidence; required checks still needed'
            : 'No learning evidence recorded yet';
  return {
    category,
    label,
    evidence,
    missing,
    note: 'Not enough evidence for a reliable percentage.',
  };
};
